import React, { useEffect, useState } from 'react';
import { ExternalDataService } from '../../services/external-data';
import { Props } from '../types';

interface SafetyOfficerResponse {
  Items: unknown[],
  Page: number;
  Total: number;
}

// eslint-disable-next-line max-len
const sortAlphabeticallyByValue = (key: string) => (a: unknown, b: unknown) => a[key]?.localeCompare(b[key]);
const buildDetails = (object: Record<string, string>, keyPattern: string): string[] => Object
  .keys(object)
  .sort()
  .filter((key) => key.includes(keyPattern) && object[key] && object[key].trim() !== '')
  .map((key) => object[key]);

export const RocheSafetyOfficersOverview = ({ slot }: Props): JSX.Element => {
  const [state, setState] = useState({ isFetching: false, items: [] });

  useEffect(() => {
    setState((prevState) => ({ ...prevState, isFetching: true }));
    ExternalDataService.getSafetyOfficers()
      .then(async (result: SafetyOfficerResponse) => {
        setState((prevState) => ({ ...prevState, items: result.Items }));
      })
      .finally(() => setState((prevState) => ({ ...prevState, isFetching: false })));
  }, []);

  if (state.isFetching) {
    return (<roche-loading-spinner></roche-loading-spinner>);
  }

  if (!state.items.length) {
    return (<roche-empty-state></roche-empty-state>);
  }

  return (
    <roche-accordion-tabs
      type="accordion"
      slot={slot}
      /*
       * Hack to trigger a render of the accordion once children have loaded.
       * TODO: On component library, add a prop `can-initialize` to the component.
       */
      start-position={state.items.length ? 1 : 0}
    >
      {state.items
        .sort(sortAlphabeticallyByValue('country_name'))
        .map((location, index) => {
          const addressLines = buildDetails(location, 'address_line').map((address) => `${address}<br/>`);
          const [name, ...address] = addressLines;
          const telephoneNumbers = buildDetails(location, 'telephone_number');
          const faxNumbers = buildDetails(location, 'fax_number');
          const emails = buildDetails(location, 'email');
          const mobileNumbers = buildDetails(location, 'mobile_number');

          return (
            <roche-accordion-tabs-item
              key={index}
              headline={location.country_name}
            >
              <roche-headline level={3}>
                <h3 dangerouslySetInnerHTML={{ __html: name }}></h3>
              </roche-headline>

              {/* TODO: remove this "spacer" and address this edge case on component library */}
              <div style={{ marginBottom: '16px' }} />

              <roche-side-by-side
                equal-ratio
                vertical-alignment="top"
              >
                <roche-richtext>
                  <p
                    dangerouslySetInnerHTML={{
                      __html: address.join(''),
                    }}>
                  </p>
                </roche-richtext>

                <address
                  style={{ display: 'flex', flexDirection: 'column' }}
                >
                  {telephoneNumbers.map((number, i) => (
                    <roche-text-link
                      key={i}
                      href={`tel:${number}`}
                      text={number}
                      left-icon="phone"
                      target="_blank"
                    />
                  ))}
                  {faxNumbers.map((number, i) => (
                    <roche-text-link
                      key={i}
                      href={`tel:${number}`}
                      text={number}
                      left-icon="fax"
                      target="_blank"
                    />
                  ))}
                  {emails.map((email, i) => (
                    <roche-text-link
                      key={i}
                      href={`mailto:${email}`}
                      text={email}
                      left-icon="email"
                      target="_blank"
                    />
                  ))}
                  {mobileNumbers.map((number, i) => (
                    <roche-text-link
                      key={i}
                      href={`tel:${number}`}
                      text={number}
                      left-icon="mobile"
                      target="_blank"
                    />
                  ))}
                </address>
              </roche-side-by-side>
            </roche-accordion-tabs-item>
          );
        })}
    </roche-accordion-tabs>
  );
};
